import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Users,
  Mail,
  Shield,
  Plus,
  Trash2,
  Check,
  X,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Settings,
  History,
  FileText,
  Globe,
  Home,
  FlaskConical,
  Calendar,
  Bell,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  Copy,
  ExternalLink,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity,
  MousePointer,
  Clock,
  Layers,
  LogOut,
  Loader2,
  BookOpen,
  Presentation,
  Sparkles,
  Image,
  Cloud,
  Code,
  Lightbulb,
  Palette,
  Wrench
} from 'lucide-react';

const API_URL = window.location.origin;

// Page configuration - COMPLETE SITEMAP (update after every deploy)
const PAGES_CONFIG = [
  // === PUBLIC PAGES ===
  { 
    id: 'landing', 
    name: 'Landing Page', 
    path: '/', 
    icon: Home, 
    public: true,
    category: 'main',
    description: 'Main homepage with hero, resources, events'
  },
  { 
    id: 'hthf', 
    name: 'High Tech High Fashion', 
    path: '/hthf', 
    icon: Calendar,
    public: true,
    category: 'events',
    description: 'HTHF event page'
  },
  { 
    id: 'womens-soiree-recap', 
    name: "Women's Soiree Recap", 
    path: '/recap/womens-soiree', 
    icon: FileText,
    public: true,
    category: 'events',
    description: 'Event recap page'
  },
  { 
    id: 'emergent', 
    name: 'Emergent Info', 
    path: '/emergent', 
    icon: Sparkles,
    public: true,
    category: 'main',
    description: 'Emergent platform info page'
  },
  { 
    id: 'hthf-partner', 
    name: 'HTHF Partner', 
    path: '/hthf-partner', 
    icon: Calendar,
    public: true,
    category: 'events',
    description: 'HTHF partner page'
  },

  // === RESOURCE PAGES (Brain Food) ===
  { 
    id: 'res-getting-started', 
    name: 'Getting Started', 
    path: '/resources/prompts/getting-started', 
    icon: BookOpen,
    public: true,
    category: 'resources',
    description: 'Getting started with Emergent guide'
  },
  { 
    id: 'res-content-management', 
    name: 'Content Management CMS', 
    path: '/resources/prompts/content-management', 
    icon: FileText,
    public: true,
    category: 'resources',
    description: 'Inline CMS setup guide with media library'
  },
  { 
    id: 'res-cloudinary', 
    name: 'Cloudinary Setup', 
    path: '/resources/prompts/cloudinary-setup', 
    icon: Cloud,
    public: true,
    category: 'resources',
    description: 'Cloudinary persistent storage guide'
  },
  { 
    id: 'res-admin-dashboard', 
    name: 'Admin Dashboard', 
    path: '/resources/prompts/admin-dashboard', 
    icon: Settings,
    public: true,
    category: 'resources',
    description: 'Admin dashboard setup guide'
  },
  { 
    id: 'res-cursor-effects', 
    name: 'Custom Cursor Effects', 
    path: '/resources/prompts/custom-cursor-effects', 
    icon: Palette,
    public: true,
    category: 'resources',
    description: 'Custom cursor effects prompt'
  },
  { 
    id: 'res-add-on-features', 
    name: 'Add-On Features', 
    path: '/resources/prompts/add-on-features', 
    icon: Wrench,
    public: true,
    category: 'resources',
    description: 'Extra feature prompts'
  },
  { 
    id: 'res-more-ideas', 
    name: 'More Prompt Ideas', 
    path: '/resources/prompts/more-ideas', 
    icon: Lightbulb,
    public: true,
    category: 'resources',
    description: 'Additional prompt ideas'
  },
  { 
    id: 'res-backend-setup', 
    name: 'Backend Setup Guide', 
    path: '/resources/guides/backend-setup', 
    icon: Code,
    public: true,
    category: 'resources',
    description: 'FastAPI backend setup guide'
  },
  { 
    id: 'res-tech-stack', 
    name: 'Tech Stack', 
    path: '/resources/stacks/tech-stack', 
    icon: Layers,
    public: true,
    category: 'resources',
    description: 'Recommended tech stack overview'
  },

  // === PROTECTED PAGES (Require Access) ===
  { 
    id: 'women-biz-soiree', 
    name: 'Women Business Soiree Workshop', 
    path: '/womenbusinesssoiree', 
    icon: FlaskConical,
    public: false,
    category: 'workshops',
    description: 'AI for Business Growth workshop content'
  },
  { 
    id: 'front-row', 
    name: 'Front Row Lab', 
    path: '/lab/frontrow/phygital', 
    icon: FlaskConical,
    public: false,
    category: 'labs',
    description: 'Behind-the-scenes startup journey'
  },
  { 
    id: 'month-1', 
    name: 'Front Row Month 1', 
    path: '/lab/frontrow/month-1', 
    icon: Calendar,
    public: false,
    category: 'labs',
    description: 'January workshop content'
  },
  { 
    id: 'datadreaming-frontrow', 
    name: 'Data Dreaming Front Row', 
    path: '/lab/frontrow/datadreaming', 
    icon: FlaskConical,
    public: false,
    category: 'labs',
    description: 'Data Dreaming build journey front row'
  },
  { 
    id: 'datadreaming-month1', 
    name: 'Data Dreaming Month 1', 
    path: '/lab/frontrow/datadreaming/month-1', 
    icon: Calendar,
    public: false,
    category: 'labs',
    description: 'Data Dreaming month 1 workshop'
  },
  { 
    id: 'soiree-recap', 
    name: 'Soiree Workshop Recap', 
    path: '/womenbusinesssoiree/recap', 
    icon: FileText,
    public: false,
    category: 'workshops',
    description: 'Workshop recap page'
  },

  // === UNLISTED PAGES (Direct link only) ===
  { 
    id: 'emergent-bir', 
    name: 'Emergent BIR Application', 
    path: '/EmergentBIR', 
    icon: Presentation,
    public: true,
    category: 'unlisted',
    description: 'Builder in Residence application page'
  },
  { 
    id: 'inline-cms', 
    name: 'Inline Content Management', 
    path: '/InlineContentManagement', 
    icon: Presentation,
    public: true,
    category: 'unlisted',
    description: 'CMS workshop presentation page'
  },

  // === SYSTEM PAGES ===
  { 
    id: 'admin', 
    name: 'Admin Dashboard', 
    path: '/admin', 
    icon: Settings,
    public: false,
    category: 'system',
    description: 'Admin dashboard and site management'
  },
];

// Admin Dashboard Component
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated, isAdmin: authIsAdmin, login, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('analytics');
  
  // Check for magic link fallback (for when CORS blocks auth API)
  const magicLinkEmail = localStorage.getItem('soiree_email');
  const isMagicLinkAdmin = magicLinkEmail && magicLinkEmail.toLowerCase() === 'me@iamnaomithomas.com';
  
  // Use auth context OR magic link fallback
  const isAdmin = authIsAdmin || isMagicLinkAdmin;
  const effectiveIsAuthenticated = isAuthenticated || isMagicLinkAdmin;
  
  // Admin emails state
  const [adminEmails, setAdminEmails] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  
  // Page access state
  const [pageAccessLists, setPageAccessLists] = useState({});
  const [selectedPage, setSelectedPage] = useState(null);
  const [newAccessEmail, setNewAccessEmail] = useState('');
  const [addingAccess, setAddingAccess] = useState(false);
  
  // Edit history state
  const [editHistory, setEditHistory] = useState([]);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  
  // Page publish status state
  const [pagePublishStatus, setPagePublishStatus] = useState(() => {
    const saved = localStorage.getItem('dd_page_publish_status');
    return saved ? JSON.parse(saved) : {
      'hthf': false,
      'hthf-partner': false
    };
  });

  // Load data when authorized
  useEffect(() => {
    if (effectiveIsAuthenticated && isAdmin) {
      loadAdminEmails();
      loadPageAccessLists();
      loadEditHistory();
      loadAnalytics();
    }
  }, [effectiveIsAuthenticated, isAdmin]);

  const loadAdminEmails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails`);
      const data = await response.json();
      setAdminEmails(data.all || []);
    } catch (error) {
      console.error('Failed to load admin emails:', error);
    }
  };

  const loadPageAccessLists = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/access-lists`);
      const data = await response.json();
      setPageAccessLists(data.lists || {});
    } catch (error) {
      console.error('Failed to load access lists:', error);
    }
  };

  const loadEditHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/history`);
      const data = await response.json();
      setEditHistory(data.history || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics`);
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) return;
    setAddingAdmin(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newAdminEmail.trim() }),
      });
      
      if (response.ok) {
        setNewAdminEmail('');
        loadAdminEmails();
      }
    } catch (error) {
      console.error('Failed to add admin:', error);
    }
    setAddingAdmin(false);
  };

  const handleRemoveAdmin = async (email) => {
    if (!confirm(`Remove admin access for ${email}?`)) return;
    
    try {
      await fetch(`${API_URL}/api/admin/emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      loadAdminEmails();
    } catch (error) {
      console.error('Failed to remove admin:', error);
    }
  };

  const handleAddPageAccess = async () => {
    if (!newAccessEmail.trim() || !selectedPage) return;
    setAddingAccess(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/access-lists/${selectedPage}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newAccessEmail.trim() }),
      });
      
      if (response.ok) {
        setNewAccessEmail('');
        loadPageAccessLists();
      }
    } catch (error) {
      console.error('Failed to add access:', error);
    }
    setAddingAccess(false);
  };

  const handleRemovePageAccess = async (pageId, email) => {
    try {
      await fetch(`${API_URL}/api/admin/access-lists/${pageId}/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      loadPageAccessLists();
    } catch (error) {
      console.error('Failed to remove access:', error);
    }
  };

  // Toggle page publish status
  const togglePagePublish = (pageId) => {
    const newStatus = {
      ...pagePublishStatus,
      [pageId]: !pagePublishStatus[pageId]
    };
    setPagePublishStatus(newStatus);
    localStorage.setItem('dd_page_publish_status', JSON.stringify(newStatus));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Filter emails based on search
  const filteredAdmins = adminEmails.filter(admin => 
    admin.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!effectiveIsAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
          <div className="w-16 h-16 rounded-full bg-purple-500/20 mx-auto mb-6 flex items-center justify-center">
            <Shield className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-400 mb-8">Sign in with Google to access the admin panel.</p>
          <button 
            onClick={() => login(window.location.href)}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white text-gray-900 rounded-xl font-medium hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 text-sm mt-6 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Authenticated but not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Not Approved</h1>
          <p className="text-gray-500 mb-2">Your account is not approved for admin access.</p>
          <p className="text-sm text-gray-400 mb-6">Signed in as: {user?.email}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => { loadAdminEmails(); loadPageAccessLists(); loadEditHistory(); loadAnalytics(); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'publish', label: 'Publish Status', icon: Globe },
            { id: 'admins', label: 'Admin Emails', icon: Shield },
            { id: 'pages', label: 'Page Access', icon: Lock },
            { id: 'links', label: 'All Links', icon: Globe },
            { id: 'history', label: 'Edit History', icon: History },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-purple-600" />
                  </div>
                  {analytics?.overview?.growthPercent !== 0 && (
                    <span className={`text-xs font-medium flex items-center gap-1 ${
                      analytics?.overview?.growthPercent > 0 ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {analytics?.overview?.growthPercent > 0 ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {analytics?.overview?.growthPercent > 0 ? '+' : ''}{analytics?.overview?.growthPercent}%
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview?.totalViews || 0}</p>
                <p className="text-sm text-gray-500">Total Page Views</p>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Activity className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-xs text-blue-600 font-medium">Today</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview?.todayViews || 0}</p>
                <p className="text-sm text-gray-500">Views Today</p>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-xs text-green-600 font-medium">Unique</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview?.uniqueVisitors || 0}</p>
                <p className="text-sm text-gray-500">Unique Visitors</p>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-amber-600" />
                  </div>
                  <span className="text-xs text-amber-600 font-medium">7 Days</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview?.weekViews || 0}</p>
                <p className="text-sm text-gray-500">This Week</p>
              </div>
            </div>

            {/* Top Pages & Daily Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Pages */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Top Pages
                </h2>
                {(!analytics?.topPages || analytics.topPages.length === 0) ? (
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No page views yet</p>
                    <p className="text-xs text-gray-400 mt-1">Visit some pages to see stats</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {analytics.topPages.map((page, idx) => {
                      const maxViews = analytics.topPages[0]?.views || 1;
                      const percentage = (page.views / maxViews) * 100;
                      return (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                              {page.path === '/' ? 'Home' : page.path}
                            </span>
                            <span className="text-sm text-gray-500">{page.views} views</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Daily Views Chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  Last 7 Days
                </h2>
                {(!analytics?.dailyViews || analytics.dailyViews.length === 0) ? (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No data yet</p>
                  </div>
                ) : (
                  <div className="flex items-end justify-between h-40 gap-2">
                    {analytics.dailyViews.map((day, idx) => {
                      const maxViews = Math.max(...analytics.dailyViews.map(d => d.views)) || 1;
                      const height = (day.views / maxViews) * 100;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                          <div className="w-full flex items-end justify-center" style={{ height: '120px' }}>
                            <div 
                              className="w-full max-w-[40px] bg-purple-500 rounded-t transition-all duration-500"
                              style={{ height: `${Math.max(height, 5)}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-gray-500">
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Recent Page Views & Top Referrers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Views */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Recent Page Views
                </h2>
                {(!analytics?.recentViews || analytics.recentViews.length === 0) ? (
                  <div className="text-center py-8">
                    <Eye className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No page views yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {analytics.recentViews.map((view, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <MousePointer className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {view.page_title || view.page_path}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(view.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Top Referrers */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-600" />
                  Top Referrers
                </h2>
                {(!analytics?.topReferrers || analytics.topReferrers.length === 0) ? (
                  <div className="text-center py-8">
                    <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No referrer data yet</p>
                    <p className="text-xs text-gray-400 mt-1">Traffic sources will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {analytics.topReferrers.map((ref, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <span className="text-sm text-gray-700 truncate max-w-[250px]">
                          {ref.referrer}
                        </span>
                        <span className="text-sm font-medium text-purple-600">{ref.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Publish Status Tab */}
        {activeTab === 'publish' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-600" />
                Page Publish Status
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Control which pages are visible to the public. Unpublished pages will show a "Coming Soon" message.
              </p>
              
              {/* Publishable Pages */}
              <div className="space-y-4">
                {/* HTHF Page */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      pagePublishStatus['hthf'] ? 'bg-green-100' : 'bg-gray-200'
                    }`}>
                      <Calendar className={`w-6 h-6 ${pagePublishStatus['hthf'] ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">High Tech High Fashion</h3>
                      <p className="text-sm text-gray-500">/hthf - Event page</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Private Badge */}
                    {pagePublishStatus['hthf'] && localStorage.getItem('hthf_private') === 'true' && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        Private
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      pagePublishStatus['hthf'] 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {pagePublishStatus['hthf'] ? 'Published' : 'Unpublished'}
                    </span>
                    <button
                      onClick={() => togglePagePublish('hthf')}
                      className={`p-2 rounded-lg transition-colors ${
                        pagePublishStatus['hthf']
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                      }`}
                    >
                      {pagePublishStatus['hthf'] ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* HTHF Private Toggle - Only show if published */}
                {pagePublishStatus['hthf'] && (
                  <div className="flex items-center justify-between p-4 rounded-xl border border-blue-200 bg-blue-50 ml-8">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <EyeOff className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Hide from Homepage</h4>
                        <p className="text-xs text-gray-500">Page is accessible but hidden from "Upcoming" section</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const isPrivate = localStorage.getItem('hthf_private') === 'true';
                        localStorage.setItem('hthf_private', (!isPrivate).toString());
                        window.location.reload();
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        localStorage.getItem('hthf_private') === 'true'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {localStorage.getItem('hthf_private') === 'true' ? 'Private ✓' : 'Make Private'}
                    </button>
                  </div>
                )}

                {/* HTHF Partner Page */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      pagePublishStatus['hthf-partner'] ? 'bg-green-100' : 'bg-gray-200'
                    }`}>
                      <Users className={`w-6 h-6 ${pagePublishStatus['hthf-partner'] ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">HTHF Partner/Sponsor</h3>
                      <p className="text-sm text-gray-500">/hthf-partner - Sponsorship page</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      pagePublishStatus['hthf-partner'] 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {pagePublishStatus['hthf-partner'] ? 'Published' : 'Unpublished'}
                    </span>
                    <button
                      onClick={() => togglePagePublish('hthf-partner')}
                      className={`p-2 rounded-lg transition-colors ${
                        pagePublishStatus['hthf-partner']
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                      }`}
                    >
                      {pagePublishStatus['hthf-partner'] ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Emails Tab */}
        {activeTab === 'admins' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Admin Emails
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                These users have full admin access including content editing and dashboard access.
              </p>
              
              {/* Add Admin Form */}
              <div className="flex gap-3 mb-6">
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAdmin()}
                />
                <button
                  onClick={handleAddAdmin}
                  disabled={addingAdmin || !newAdminEmail.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {addingAdmin ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Add Admin
                </button>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search admins..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Admin List */}
              <div className="space-y-2">
                {filteredAdmins.map((admin) => (
                  <div 
                    key={admin.email}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{admin.email}</p>
                        <p className="text-xs text-gray-500">
                          Added {admin.added_by === 'system' ? 'by system' : `by ${admin.added_by}`} • {new Date(admin.added_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${admin.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleRemoveAdmin(admin.email)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove admin"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {filteredAdmins.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No admin emails found</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Page Access Tab */}
        {activeTab === 'pages' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pages List */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Protected Pages</h2>
              <div className="space-y-2">
                {PAGES_CONFIG.filter(p => !p.public).map((page) => (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPage(page.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all ${
                      selectedPage === page.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedPage === page.id ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        <page.icon className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{page.name}</p>
                        <p className="text-xs text-gray-500">{page.path}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                        {(pageAccessLists[page.id] || []).length + adminEmails.length} users
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Selected Page Access */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {selectedPage ? (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Access List: {PAGES_CONFIG.find(p => p.id === selectedPage)?.name}
                  </h2>
                  
                  {/* Add Access Form */}
                  <div className="flex gap-3 mb-6">
                    <input
                      type="email"
                      value={newAccessEmail}
                      onChange={(e) => setNewAccessEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddPageAccess()}
                    />
                    <button
                      onClick={handleAddPageAccess}
                      disabled={addingAccess || !newAccessEmail.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {addingAccess ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      Add
                    </button>
                  </div>

                  {/* Access List */}
                  <div className="space-y-2">
                    {/* Show admin emails first (they have access to everything) */}
                    {adminEmails.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Admins (access to all pages)</p>
                        {adminEmails.map((admin) => (
                          <div 
                            key={admin.email}
                            className="flex items-center justify-between p-3 rounded-lg bg-purple-50 mb-1"
                          >
                            <div className="flex items-center gap-3">
                              <Shield className="w-4 h-4 text-purple-500" />
                              <span className="text-gray-900">{admin.email}</span>
                            </div>
                            <span className="text-xs text-purple-600">Admin</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Page-specific access */}
                    {(pageAccessLists[selectedPage] || []).length > 0 && (
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Page-specific access</p>
                    )}
                    {(pageAccessLists[selectedPage] || []).map((item) => (
                      <div 
                        key={item.email}
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{item.email}</span>
                        </div>
                        <button
                          onClick={() => handleRemovePageAccess(selectedPage, item.email)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    {(pageAccessLists[selectedPage] || []).length === 0 && adminEmails.length === 0 && (
                      <div className="text-center py-8">
                        <Lock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">No users have access yet</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Select a page to manage access</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* All Links Tab */}
        {activeTab === 'links' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-600" />
                  All Site Links
                </h2>
                <span className="text-xs text-gray-400">{PAGES_CONFIG.length} pages total</span>
              </div>
              
              {/* Main Pages */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Main Pages</h3>
                <div className="space-y-3">
                  {PAGES_CONFIG.filter(p => p.category === 'main').map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                          <page.icon className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{page.name}</p>
                          <p className="text-xs text-gray-500">{page.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-1 bg-gray-100 rounded text-sm text-gray-600">{page.path}</code>
                        <button onClick={() => copyToClipboard(`${window.location.origin}${page.path}`)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Copy link">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                        <Link to={page.path} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Open page">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Events */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Events</h3>
                <div className="space-y-3">
                  {PAGES_CONFIG.filter(p => p.category === 'events').map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <page.icon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{page.name}</p>
                          <p className="text-xs text-gray-500">{page.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-1 bg-gray-100 rounded text-sm text-gray-600">{page.path}</code>
                        <button onClick={() => copyToClipboard(`${window.location.origin}${page.path}`)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Copy link">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                        <Link to={page.path} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Open page">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brain Food Resources */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Brain Food Resources</h3>
                <div className="space-y-3">
                  {PAGES_CONFIG.filter(p => p.category === 'resources').map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 rounded-lg border border-purple-200 bg-purple-50/30 hover:border-purple-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <page.icon className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{page.name}</p>
                          <p className="text-xs text-gray-500">{page.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-1 bg-white rounded text-sm text-gray-600">{page.path}</code>
                        <button onClick={() => copyToClipboard(`${window.location.origin}${page.path}`)} className="p-2 hover:bg-purple-100 rounded-lg transition-colors" title="Copy link">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                        <Link to={page.path} className="p-2 hover:bg-purple-100 rounded-lg transition-colors" title="Open page">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Protected: Workshops */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Workshops (Require Access)</h3>
                <div className="space-y-3">
                  {PAGES_CONFIG.filter(p => p.category === 'workshops').map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50/50 hover:border-amber-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <page.icon className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-2">
                            {page.name}
                            <Lock className="w-3 h-3 text-amber-500" />
                          </p>
                          <p className="text-xs text-gray-500">{page.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                          {(pageAccessLists[page.id] || []).length + adminEmails.length} users
                        </span>
                        <code className="px-3 py-1 bg-white rounded text-sm text-gray-600">{page.path}</code>
                        <button onClick={() => copyToClipboard(`${window.location.origin}${page.path}`)} className="p-2 hover:bg-amber-100 rounded-lg transition-colors" title="Copy link">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                        <Link to={page.path} className="p-2 hover:bg-amber-100 rounded-lg transition-colors" title="Open page">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Protected: Labs */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Labs (Require Access)</h3>
                <div className="space-y-3">
                  {PAGES_CONFIG.filter(p => p.category === 'labs').map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50/50 hover:border-amber-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <page.icon className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-2">
                            {page.name}
                            <Lock className="w-3 h-3 text-amber-500" />
                          </p>
                          <p className="text-xs text-gray-500">{page.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                          {(pageAccessLists[page.id] || []).length + adminEmails.length} users
                        </span>
                        <code className="px-3 py-1 bg-white rounded text-sm text-gray-600">{page.path}</code>
                        <button onClick={() => copyToClipboard(`${window.location.origin}${page.path}`)} className="p-2 hover:bg-amber-100 rounded-lg transition-colors" title="Copy link">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                        <Link to={page.path} className="p-2 hover:bg-amber-100 rounded-lg transition-colors" title="Open page">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Unlisted Pages */}
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Unlisted Pages (Direct Link Only)</h3>
                <div className="space-y-3">
                  {PAGES_CONFIG.filter(p => p.category === 'unlisted').map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 rounded-lg border border-teal-200 bg-teal-50/50 hover:border-teal-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                          <page.icon className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-2">
                            {page.name}
                            <EyeOff className="w-3 h-3 text-teal-500" />
                          </p>
                          <p className="text-xs text-gray-500">{page.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-700">unlisted</span>
                        <code className="px-3 py-1 bg-white rounded text-sm text-gray-600">{page.path}</code>
                        <button onClick={() => copyToClipboard(`${window.location.origin}${page.path}`)} className="p-2 hover:bg-teal-100 rounded-lg transition-colors" title="Copy link">
                          <Copy className="w-4 h-4 text-gray-500" />
                        </button>
                        <Link to={page.path} className="p-2 hover:bg-teal-100 rounded-lg transition-colors" title="Open page">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* System Pages */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">System</h3>
                <div className="space-y-3">
                  {PAGES_CONFIG.filter(p => p.category === 'system').map((page) => (
                    <div key={page.id} className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50/50 hover:border-gray-300 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <page.icon className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{page.name}</p>
                          <p className="text-xs text-gray-500">{page.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-1 bg-white rounded text-sm text-gray-600">{page.path}</code>
                        <Link to={page.path} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Open page">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Quick Reference */}
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-6">
              <h3 className="font-semibold text-purple-900 mb-3">Quick Reference</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-purple-700 font-medium">Base URL:</p>
                  <code className="text-purple-600">{window.location.origin}</code>
                </div>
                <div>
                  <p className="text-purple-700 font-medium">Admin Dashboard:</p>
                  <code className="text-purple-600">/admin</code>
                </div>
                <div>
                  <p className="text-purple-700 font-medium">Magic Link:</p>
                  <code className="text-purple-600">/auth/magic?key=...</code>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Edit History
            </h2>
            
            {editHistory.length === 0 ? (
              <div className="text-center py-12">
                <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No edit history yet</p>
                <p className="text-xs text-gray-400 mt-1">Changes will appear here when content is edited</p>
              </div>
            ) : (
              <div className="space-y-4">
                {editHistory.slice().reverse().map((entry) => (
                  <div key={entry.id} className="p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                        {entry.changes.length} changes
                      </span>
                    </div>
                    <div className="space-y-2">
                      {entry.changes.map((change, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-medium text-gray-700">{change.sectionId}</span>
                          <span className="text-gray-400 mx-2">→</span>
                          <span className="text-gray-600">{change.field}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
